import { useCallback, useContext, useEffect, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { LoadingIndicator, Notification } from "@k8slens/lds";
import { FormSwitchGroup } from "@k8slens/lds-form";
import { type SwitchValue } from "@k8slens/lds-form/lib/es/FormSwitchGroup/FormSwitchGroup";
import { navigation } from "@k8slens/lds-icons";

import { useBusiness } from "src/hooks/useBusiness";
import { useAnalytics } from "src/hooks/useAnalytics";
import { ProfileContext } from "src/providers/profile-provider";
import { useIsDomainMatchingSettingEnabled } from "src/hooks/useIsDomainMatchingSettingEnabled";

import { BusinessContext } from "src/components/Business/Base";
import PageHeader from "src/components/PageHeader/PageHeader";
import { getStringLength } from "src/services/getStringLength";

import styles from "./page.module.css";
import TrackedAnchor from "src/components/TrackedAnchor/TrackedAnchor";

const DOMAIN_MATCHING_ENABLED = "domain-matching-enabled";
const AUTO_ACCEPT_JOIN_REQUESTS = "auto-accept-join-requests-enabled";

const updateAction = "Update Domain Matching Settings";

const { PreviewIcon } = navigation;

const CHARS_PER_LINE = 140;
const BASE_HEIGHT = 10; // rem
const calculateTextAreaHeight = (length: number, messageForUsers: string): string => {
  const newLineCount = (messageForUsers.match(/\n/g) || []).length;
  const lines = Math.ceil(length / CHARS_PER_LINE) + newLineCount;

  const additionalHeight = Math.max(0, lines - 4); // 4 lines fit in 10rem by default

  return `${BASE_HEIGHT + additionalHeight * 1.3}rem`;
};

const BusinessDomainMatching = () => {
  const history = useHistory();
  const { track } = useAnalytics();
  const { profile } = useContext(ProfileContext);
  const [messageForUsers, setMessageForUsers] = useState<string>("");
  const { businessId } = useContext(BusinessContext);
  const { updateBusiness, errorUpdatingBusiness, updatingBusiness, business, loadingBusiness } = useBusiness(
    businessId,
    (updatedBusiness) => {
      setMessageForUsers(updatedBusiness.emailDomainWelcomeMessage);
    },
  );
  const showDomainMatchingSettings = useIsDomainMatchingSettingEnabled(businessId);

  const loadingCurrentBusinessPlan = showDomainMatchingSettings === undefined;

  const [switches, setSwitches] = useState<Array<SwitchValue>>([]);

  const handleSwitchChange = useCallback(
    async (nextSwitches: Array<SwitchValue>, keys: Array<string>) => {
      const changed = nextSwitches.filter(({ id }) => keys.includes(id));

      if (changed.find((d) => d.id === DOMAIN_MATCHING_ENABLED || d.id === AUTO_ACCEPT_JOIN_REQUESTS)) {
        track(updateAction);

        if (changed.find((d) => d.id === DOMAIN_MATCHING_ENABLED)) {
          updateBusiness({
            emailDomainMatchingEnabled: changed.find((s) => s.id === DOMAIN_MATCHING_ENABLED)?.checked,
            emailDomainWelcomeMessage: messageForUsers,
          });
        }

        if (changed.find((d) => d.id === AUTO_ACCEPT_JOIN_REQUESTS)) {
          updateBusiness({
            autoAcceptJoinRequests: changed.find((s) => s.id === AUTO_ACCEPT_JOIN_REQUESTS)?.checked,
            emailDomainWelcomeMessage: messageForUsers,
          });
        }
      }
    },
    [messageForUsers, track, updateBusiness],
  );

  const userCompanyEmailDomain = profile?.companyEmailDomain;
  const disabled = !(showDomainMatchingSettings && userCompanyEmailDomain);

  useEffect(() => {
    if (!business) {
      return;
    }
    let emailDomain = userCompanyEmailDomain || "yours";

    if (business.emailDomainMatchingEnabled) {
      emailDomain = business.emailDomain || emailDomain;
    }
    setSwitches([
      {
        id: DOMAIN_MATCHING_ENABLED,
        label: "Enable Domain Matching",
        description: `Lens users can discover your organization and request to join if their verified email domain matches ${emailDomain}.`,
        checked: business.emailDomainMatchingEnabled,
      },
      {
        id: AUTO_ACCEPT_JOIN_REQUESTS,
        label: "Automatically accept join requests",
        description:
          "Users requesting to join your organization through domain matching are automatically accepted and no manual approval is required.",
        checked: business.autoAcceptJoinRequests,
      },
    ]);
  }, [business, userCompanyEmailDomain]);

  useEffect(() => {
    if (business?.emailDomainWelcomeMessage) {
      setMessageForUsers(business.emailDomainWelcomeMessage);
    }
  }, [business?.emailDomainWelcomeMessage]);

  if (loadingCurrentBusinessPlan) {
    return <LoadingIndicator size="xxl" className={styles.loadingIndicator} />;
  }

  if (!showDomainMatchingSettings) {
    return <Redirect to={`/business/${businessId}`} />;
  }

  return (
    <>
      {errorUpdatingBusiness ? (
        <Notification flashDuration={1000} level="error" message={errorUpdatingBusiness.message} type="flash" />
      ) : null}
      <PageHeader
        id="domain-matching-settings"
        title="Domain Matching"
        subtitle={
          <>
            Streamline user onboarding by leveraging domain matching to automatically connect users with your
            organization in Lens. These settings allow you to enable domain based access, automate user joins, and
            customize messaging for a seamless onboarding experience.
          </>
        }
      />
      <section>
        {business && (
          <FormSwitchGroup
            label=""
            values={switches || []}
            onChange={handleSwitchChange}
            disabled={disabled || loadingBusiness || updatingBusiness}
          />
        )}
        <div className="flex flex-col">
          <label htmlFor="messageForUsers" className="lds-form-switch-group--switch-label">
            Message to new users
          </label>
          <p className="lds-form-switch-group--description">
            A custom message in plain text or{" "}
            <TrackedAnchor href="https://commonmark.org/help/" target="_blank" rel="noreferrer">
              markdown
            </TrackedAnchor>{" "}
            format to guide new users on getting started. Provide instructions on where to find kubeconfig files, steps
            to add clusters into Lens, links to internal wiki pages, or details about internal license onboarding. This
            message will appear after users request access to join your organization.
          </p>
        </div>
        <div className="relative">
          <textarea
            value={messageForUsers}
            className={styles.messageForUsers}
            style={{
              minHeight: calculateTextAreaHeight(getStringLength(messageForUsers), messageForUsers),
            }}
            id="messageForUsers"
            name="messageForUsers"
            onChange={(event) => {
              const value = event.target.value;

              setMessageForUsers(value);
            }}
            onBlur={() => {
              updateBusiness({ emailDomainWelcomeMessage: messageForUsers });
            }}
            maxLength={500}
          ></textarea>
          <button
            title={"Preview Message"}
            aria-label="Preview Message"
            className={styles.previewButton}
            onMouseDown={(event) => {
              // Prevent blur event on textarea
              event.preventDefault();

              updateBusiness({ emailDomainWelcomeMessage: messageForUsers });

              history.push(`/business/${businessId}/domain-matching/welcome-message/preview`);
            }}
          >
            <PreviewIcon />
          </button>
          <span className={styles.messageCharCount}>{`${getStringLength(messageForUsers)}/500`}</span>
        </div>
      </section>
      {(updatingBusiness || loadingBusiness) && <LoadingIndicator size="xxl" className={styles.loadingIndicator} />}
    </>
  );
};

export default BusinessDomainMatching;
